/**
 * JWT store · spec 05 §L1494-L1530 + T302 expansion.
 *
 * Module-level singleton holding the latest minted NATS JWT plus its
 * companion seed/server-list (collectively "creds"). The authenticator
 * callback (`buildConnectConfig` in `connect-config.ts`) reads
 * `current()` synchronously on every reconnect attempt — it MUST NOT
 * block the nats-core reconnect path, so this module never awaits.
 *
 * Two persistence layers:
 *   - In-memory cache       — every platform; the authenticator closure
 *                             reads from here on every reconnect attempt.
 *   - Platform secureStore  — RN ONLY (Keychain / Keystore). The web
 *                             secureStorage slot is a `no-op throws` shim
 *                             per file 05 §2848 — JWT stays in-memory.
 *
 * Concurrency: serialised via `reconnect-mutex` so a mid-session refresh
 * (`set(...)`) can never race a reconnect attempt's read.
 *
 * HMR pinning: pinned to `globalThis.__morfi_jwt_store__` via the
 * `??=` idempotent assignment so a dev-server hot-reload doesn't
 * double-instantiate the store (which would silently keep the OLD
 * reconnect-mutex bound to the OLD JWT cell — refresh → reconnect deadlock).
 */

export interface JwtCreds {
  /** The signed JWT body. Empty string is rejected. */
  readonly jwt: string;
  /** Optional NKey seed (RN deep-link mint path). */
  readonly seed?: string;
  /** NATS server URLs the caller should connect to. */
  readonly servers?: readonly string[];
  /** Token-class for refresh scheduling (`anon` = 2h, `user` = 4h). */
  readonly kind?: 'anon' | 'user';
  /**
   * Server-authorized subscribe roots from the mint response. The
   * provider's wire-subscription effect reads this to open one SUB per
   * root — without it a user mint would silently SUB only anon roots.
   */
  readonly allowSub?: readonly string[];
}

/**
 * Async secureStore primitive backed by the platform adapter (Keychain
 * on iOS, Keystore on Android, no-op-throws on web).
 */
export interface SecureStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export interface JwtStore {
  /** Synchronous read — called from the authenticator closure. */
  current(): JwtCreds | null;
  /** Back-compat sync read returning the JWT body only. */
  read(): string;
  /** Swap the cached credentials after a successful mint. */
  set(next: JwtCreds): void;
  /** Back-compat alias matching the pre-T302 API. */
  update(next: string): void;
  /** Bootstrap from platform secureStore (RN). No-op on web. */
  restore(secureStore?: SecureStore): Promise<void>;
  /** Persist the current creds to secureStore. No-op on web. */
  persist(secureStore?: SecureStore): Promise<void>;
  /** Drop the cached creds (sign-out / mint-failed cascade). */
  clear(): void;
  /** Last-mint timestamp (ms epoch) for tests / telemetry. */
  mintedAt(): number;
}

const STORE_KEY = 'morfi.jwt.creds.v1';
const PIN_KEY = '__morfi_jwt_store__' as const;

type GlobalWithStore = typeof globalThis & {
  [PIN_KEY]?: JwtStore;
};

function makeStore(initial?: JwtCreds | string): JwtStore {
  const seed: JwtCreds | null =
    typeof initial === 'string'
      ? initial.length > 0
        ? { jwt: initial }
        : null
      : (initial ?? null);
  let creds: JwtCreds | null = seed;
  let mintedAtMs = seed === null ? 0 : Date.now();

  const api: JwtStore = {
    current: () => creds,
    read: () => creds?.jwt ?? '',
    set: (next: JwtCreds) => {
      if (typeof next.jwt !== 'string' || next.jwt.length === 0) {
        throw new TypeError('jwt-store.set: jwt must be a non-empty string');
      }
      creds = next;
      mintedAtMs = Date.now();
    },
    // Preserve the companion fields (`kind`, `servers`, `allowSub`) across
    // a JWT-body-only rotation — a mid-session refresh keeps the same
    // identity scope, so wiping `allowSub` here would drop the wire-sub
    // back to the anon roots on the next read.
    update: (next: string) => api.set({ ...(creds ?? {}), jwt: next }),
    restore: async (secureStore?: SecureStore) => {
      if (secureStore === undefined) return;
      try {
        const raw = await secureStore.get(STORE_KEY);
        if (raw === null) return;
        const parsed = JSON.parse(raw) as JwtCreds;
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          typeof parsed.jwt === 'string' &&
          parsed.jwt.length > 0
        ) {
          creds = parsed;
          mintedAtMs = Date.now();
        }
      } catch {
        // Corrupt / unreadable secureStore entry — drop on the floor;
        // the next mint will refresh. Never throw on bootstrap.
      }
    },
    persist: async (secureStore?: SecureStore) => {
      if (secureStore === undefined || creds === null) return;
      try {
        await secureStore.set(STORE_KEY, JSON.stringify(creds));
      } catch {
        // Web secureStorage is `no-op throws` per file 05 §2848.
        // Persistence is best-effort; refresh on next boot is fine.
      }
    },
    clear: () => {
      creds = null;
      mintedAtMs = 0;
    },
    mintedAt: () => mintedAtMs,
  };
  return api;
}

/**
 * Singleton accessor. The first call creates the store and pins it via
 * `??=` on globalThis. Subsequent calls (including post-HMR) reuse it.
 *
 * The optional `seed` is honoured ONLY on the very first call — after
 * that the pin is sticky. Tests can call `__resetJwtStoreForTests()`
 * to get a fresh store.
 */
export function getJwtStore(seed?: JwtCreds | string): JwtStore {
  const g = globalThis as GlobalWithStore;
  g[PIN_KEY] ??= makeStore(seed);
  return g[PIN_KEY];
}

/**
 * Back-compat factory matching the pre-T302 API. Returns a NEW store
 * (no globalThis pin) so existing call sites that wanted a fresh
 * instance keep working. Prefer `getJwtStore()` in new code.
 */
export function createJwtStore(initial: string | JwtCreds): JwtStore {
  return makeStore(initial);
}

/** Test-only reset. Production code must not call this. */
export function __resetJwtStoreForTests(): void {
  const g = globalThis as GlobalWithStore;
  delete g[PIN_KEY];
}

/** Exported for tests / persistence backends that want to mirror the key. */
export const JWT_STORE_SECURE_KEY: string = STORE_KEY;
